import * as React from "react";
import {
  List,
  Datagrid,
  TextField,
  RichTextField,
  BooleanField,
  useListContext,
  TopToolbar,
  CreateButton,
  ExportButton,
  Create,
  sanitizeListRestProps,
  SimpleForm,
  TextInput,
  NumberInput,
  BooleanInput,
  Show,
  SimpleShowLayout,
  Edit,
  required,
  AutocompleteInput,
  Filter,
  NullableBooleanInput,
  FunctionField,
  SelectField,
} from "react-admin";
import { getSizeOptions } from "../../api";
import { useEffect, useRef, useState } from "react";
import { PasswordField } from "./SessionsList";

const choices = [
  { id: 0, name: "No restriction" },
  { id: 1, name: "User" },
  { id: 2, name: "Pilot" },
  { id: 4, name: "Internal" },
  { id: 255, name: "Admin" },
];

export function TemplatesList(props: any) {
  return (
    <List {...props} title="VM Templates" filters={<TemplateFilter />}>
      <Datagrid rowClick="show">
        <TextField source="edition" />
        <BooleanField source="isActive" />
        <TextField source="model" />
        <SelectField
          source="restrictedTo"
          choices={choices}
          label="min. User permission"
        />
        <TextField source="size" />
        <TextField source="price" />
        <TextField source="computerName" sortable={false} />
        <TextField source="winAdminUser" label="Admin user" sortable={false} />
        <FunctionField
          source="winAdminPassword"
          label="Admin password"
          sortable={false}
          render={(record: any) => {
            return <PasswordField text={record.winAdminPassword ?? ""} />;
          }}
        />
        <FunctionField
          source="vncPassword"
          label="VNC Password"
          sortable={false}
          render={(record: any) => {
            return <PasswordField text={record.vncPassword ?? ""} />;
          }}
        />
      </Datagrid>
    </List>
  );
}

export const TemplateCreate = (props: any) => {
  const sizeOptions = useRef<{ name: string }[]>([]);
  const [sizesLoaded, setSizesLoaded] = useState(false);

  useEffect(() => {
    !sizesLoaded &&
      getSizeOptions().then((res) => {
        sizeOptions.current = res;
        setSizesLoaded(true);
      });
  }, [sizesLoaded]);

  return (
    <Create {...props}>
      <SimpleForm redirect="list">
        <TextInput source="edition" validate={[required()]} />
        <BooleanInput source="isActive" />
        <TextInput source="model" validate={[required()]} />
        <AutocompleteInput
          source="restrictedTo"
          choices={choices}
          label="min. User permission"
        />
        <NumberInput source="price" validate={[required()]} />
        <AutocompleteInput
          source="size"
          choices={sizeOptions.current}
          optionText="name"
          optionValue="name"
          validate={[required()]}
        />
        <TextInput
          source="snapshot.diskC"
          label="Disk C"
          validate={[required()]}
        />
        <TextInput
          source="snapshot.diskD"
          label="Disk D"
          validate={[required()]}
        />
        <TextInput source="computerName" />
        <TextInput source="winAdminUser" label="Admin user" />
        <TextInput source="winAdminPassword" label="Admin password" />
        <TextInput source="vncPassword" />
      </SimpleForm>
    </Create>
  );
};

export const TemplateShow = (props: any) => (
  <Show title="Template details" {...props}>
    <SimpleShowLayout>
      <TextField source="id" />
      <TextField source="edition" />
      <BooleanField source="isActive" />
      <TextField source="model" />
      <SelectField
          source="restrictedTo"
          choices={choices}
          label="min. User permission"
        />
      <TextField source="size" />
      <TextField source="price" />
      <TextField source="computerName" />
      <TextField source="winAdminUser" label="Admin user" />
      <FunctionField
        source="winAdminPassword"
        label="Admin password"
        sortable={false}
        render={(record: any) => {
          return <PasswordField text={record.winAdminPassword ?? ""} />;
        }}
      />
      <FunctionField
        source="vncPassword"
        label="VNC Password"
        sortable={false}
        render={(record: any) => {
          return <PasswordField text={record.vncPassword ?? ""} />;
        }}
      />
      <RichTextField source="snapshot.diskC" label="Disc C" />
      <RichTextField source="snapshot.diskD" label="Disc D" />
    </SimpleShowLayout>
  </Show>
);

export const TemplateEdit = (props: any) => {
  const sizeOptions = useRef<{ name: string }[]>([]);
  const [sizesLoaded, setSizesLoaded] = useState(false);

  useEffect(() => {
    !sizesLoaded &&
      getSizeOptions().then((res) => {
        sizeOptions.current = res;
        setSizesLoaded(true);
      });
  }, [sizesLoaded]);

  return (
    <Edit title="Edit template" {...props}>
      <SimpleForm>
        <TextInput source="edition" validate={[required()]} />
        <BooleanInput source="isActive" />
        <TextInput source="model" validate={[required()]} />
        <AutocompleteInput
          source="restrictedTo"
          choices={choices}
          label="min. User permission"
        />
        <NumberInput source="price" validate={[required()]} />
        <AutocompleteInput
          source="size"
          choices={sizeOptions.current}
          optionText="name"
          optionValue="name"
          validate={[required()]}
        />
        <TextInput
          source="snapshot.diskC"
          label="Disk C"
          validate={[required()]}
        />
        <TextInput
          source="snapshot.diskD"
          label="Disk D"
          validate={[required()]}
        />
        <TextInput source="computerName" />
        <TextInput
          source="winAdminUser"
          label="Admin user"
          validate={[required()]}
        />
        <TextInput
          source="winAdminPassword"
          label="Admin password"
          validate={[required()]}
        />
        <TextInput source="vncPassword" validate={[required()]} />
      </SimpleForm>
    </Edit>
  );
};

export const ListActions = (props: any) => {
  const { className, exporter, filters, maxResults, ...rest } = props;
  const { currentSort, resource, filterValues, hasCreate, basePath, total } =
    useListContext();
  return (
    <TopToolbar className={className} {...sanitizeListRestProps(rest)}>
      {hasCreate && <CreateButton basePath={basePath} />}
      <ExportButton
        disabled={total === 0}
        resource={resource}
        sort={currentSort}
        filterValues={filterValues}
        maxResults={maxResults}
      />
    </TopToolbar>
  );
};

const TemplateFilter = (props: any) => {
  const sizeOptions = useRef<{ name: string }[]>([]);
  const [sizesLoaded, setSizesLoaded] = useState(false);

  useEffect(() => {
    !sizesLoaded &&
      getSizeOptions().then((res) => {
        sizeOptions.current = res;
        setSizesLoaded(true);
      });
  }, [sizesLoaded]);

  return (
    <Filter {...props}>
      <TextInput label="Search" source="search" alwaysOn variant="outlined" />
      <TextInput source="edition" variant="outlined" />
      <TextInput source="model" variant="outlined" />
      <NumberInput source="price" variant="outlined" />
      <NullableBooleanInput source="isActive" variant="outlined" />
      <AutocompleteInput
        source="size"
        choices={sizeOptions.current}
        optionText="name"
        optionValue="name"
        variant="outlined"
      />
    </Filter>
  );
};
